// theme.js
import { createTheme } from "@mui/material/styles";
import SvgIcon from "@mui/material/SvgIcon";

// Define custom icons as React components
const CustomIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M17.9 2.318A5 5 0 0 1 22.895 7.1l.005.217v10a5 5 0 0 1-4.783 4.995l-.217.005h-10a5 5 0 0 1-4.995-4.783l-.005-.217v-10a5 5 0 0 1 4.783-4.996l.217-.004h10Zm-.5 1.5h-9a4 4 0 0 0-4 4v9a4 4 0 0 0 4 4h9a4 4 0 0 0 4-4v-9a4 4 0 0 0-4-4Z" />
  </SvgIcon>
);

const CheckedIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M17 2a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm-1.625 7.255-4.13 4.13-1.75-1.75a.881.881 0 0 0-1.24 0c-.34.34-.34.89 0 1.24l2.38 2.37c.17.17.39.25.61.25.23 0 .45-.08.62-.25l4.75-4.75c.34-.34.34-.89 0-1.24a.881.881 0 0 0-1.24 0Z" />
  </SvgIcon>
);

const IndeterminateIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M17,2 C19.7614,2 22,4.23858 22,7 L22,7 L22,17 C22,19.7614 19.7614,22 17,22 L17,22 L7,22 C4.23858,22 2,19.7614 2,17 L2,17 L2,7 C2,4.23858 4.23858,2 7,2 L7,2 Z M15,11 L9,11 C8.44772,11 8,11.4477 8,12 C8,12.5523 8.44772,13 9,13 L15,13 C15.5523,13 16,12.5523 16,12 C16,11.4477 15.5523,11 15,11 Z" />
  </SvgIcon>
);

let theme = createTheme({
  palette: {
    primary: {
      light: "#CEE1F6",
      200: "#A0C3ED",
      300: "#6791CA",
      400: "#3A5D96",
      main: "#0F2651",
      600: "#0A1D45",
      700: "#07153A",
      800: "#040E2E",
      dark: "#020A26",
    },
    secondary: {
      // blue light
      light: "#F0F7FD",
      200: "#E2EFFC",
      300: "#D1E2F8",
      400: "#C2D5F2",
      main: "#ACC3EA",
      600: "#7D96C9",
      700: "#566EA8",
      800: "#364B87",
      dark: "#213270",
    },
    // Success Colors green
    success: {
      light: "#D3FCD2",
      200: "#A6F9AC",
      300: "#77ED8B",
      400: "#53DC78",
      main: "#22C55E",
      600: "#18A95C",
      700: "#118D57",
      800: "#0A724F",
      dark: "#065E49",
    },

    // Error Colors
    error: {
      light: "#FFE9D5",
      200: "#FFCEAC",
      300: "#FFAC82",
      400: "#FF8B63",
      main: "#FF5630",
      600: "#DB3723",
      700: "#B71D18",
      800: "#930F14",
      dark: "#7A0916",
    },

    // Warning Colors
    warning: {
      light: "#FDF9D7",
      200: "#FCF2B0",
      300: "#F8E687",
      400: "#F1D967",
      main: "#e8c538",
      600: "#C7A528",
      700: "#A7861C",
      800: "#866811",
      dark: "#6F530A",
      darker: "#5F4407",
    },

    // Grey Colors
    grey: {
      50: "#f8fafc",
      100: "#eef2f6",
      200: "#e3e8ef",
      300: "#cdd5df",
      500: "#697586",
      600: "#4b5565",
      700: "#364152",
      900: "#121926",
      neutral: "#F4F6F8",
    },

    // Blue Colors
    common: {
      100: "#F0F7FD",
      200: "#E2EFFC",
      300: "#D1E2F8",
      400: "#C2D5F2",
      500: "#ACC3EA",
      600: "#7D96C9",
      700: "#566EA8",
      800: "#364B87",
      900: "#213270",
    },

    // Dark Theme Variants
    dark: {
      background: "#1a223f",
      paper: "#111936",
      level1: "#29314f",
      level2: "#212946",
      primary: {
        light: "#eef2f6",
        main: "#2196f3",
        dark: "#1e88e5",
        200: "#90caf9",
        800: "#1565c0",
      },
      secondary: {
        light: "#d1c4e9",
        main: "#7c4dff",
        dark: "#651fff",
        200: "#b39ddb",
        800: "#6200ea",
      },
      text: {
        title: "#d7dcec",
        primary: "#bdc8f0",
        secondary: "#8492c4",
      },
    },
  },
});

theme = createTheme(theme, {
  palette: {
    text: {
      dark: theme.palette.grey[900],
    },
  },
  shape: {
    borderRadius: 4,
  },
  typography: {
    fontFamily: ['"Manrope"', "Arial", "sans-serif"].join(","),
    h6: {
      fontFamily: ['"Manrope"', "Arial", "sans-serif"].join(","),
      fontWeight: 400,
      lineHeight: 1.56,
      color: theme.palette.text.title,
      fontSize: "1rem",
    },
    h5: {
      fontFamily: ['"Manrope"', "Arial", "sans-serif"].join(","),
      fontSize: "1.125rem",
      color: theme.palette.text.title,
      fontWeight: 400,
    },
    h4: {
      fontFamily: ['"Manrope"', "Arial", "sans-serif"].join(","),
      fontSize: "1.25rem",
      lineHeight: 1.33,
      color: theme.palette.text.title,
      fontWeight: 600,
    },
    h3: {
      fontFamily: ['"Manrope"', "Arial", "sans-serif"].join(","),
      fontSize: "1.5rem",
      lineHeight: 1.5,
      color: theme.palette.text.title,
      fontWeight: 600,
    },
    h2: {
      fontFamily: ['"Manrope"', "Arial", "sans-serif"].join(","),
      fontSize: "1.85rem",
      lineHeight: 1.33,
      color: theme.palette.text.title,
      fontWeight: 700,
    },
    h1: {
      fontFamily: ['"Manrope"', "Arial", "sans-serif"].join(","),
      fontSize: "2.5rem",
      lineHeight: 1.25,
      color: theme.palette.text.title,
      fontWeight: 700,
    },
    subtitle1: {
      fontFamily: ['"Manrope"', "Arial", "sans-serif"].join(","),
      fontSize: "0.95rem",
      fontWeight: 600,
      lineHeight: 1.5,
      color: theme.palette.text.dark,
    },
    p: {
      fontFamily: ['"Manrope"', "Arial", "sans-serif"].join(","),
      fontSize: "0.975rem",
      fontWeight: 400,
      lineHeight: 1.5,
      color: theme.palette.text.dark,
    },
    a: {
      fontFamily: ['"Manrope"', "Arial", "sans-serif"].join(","),
      fontWeight: 400,
      lineHeight: 1.5,
      color: theme.palette.text.dark,
    },
    subtitle2: {
      fontFamily: ['"Manrope"', "Arial", "sans-serif"].join(","),
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: 1.57,
      color: theme.palette.text.secondary,
    },
    caption: {
      fontFamily: ['"Manrope"', "Arial", "sans-serif"].join(","),
      fontSize: "0.75rem",    // 12px
      lineHeight: 1.57,
      color: theme.palette.text.secondary,
      fontWeight: 400,
    },
    body1: {
      fontFamily: ['"Manrope"', "Arial", "sans-serif"].join(","),
      fontWeight: 400,
      lineHeight: "1.334em",
      fontSize: "1rem",       // 16px
    },
    body2: {
      fontFamily: ['"Manrope"', "Arial", "sans-serif"].join(","),
      letterSpacing: "0em",
      fontWeight: 400,
      fontSize: "0.875rem",   // 14px
      lineHeight: "1.5em",
      color: theme.palette.text.primary,
    },
    button: {
      textTransform: "none",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"Manrope", Arial, sans-serif',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          "&.MuiAccordion-root": {
            boxShadow: "none",
          },
          "&.MuiCard-root": {
            boxShadow:
              "rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px",
          },
          "&.MuiPaper-outlined": {
            borderRadius: "16px",
            borderStyle: "dashed",
            padding: "20px",
            gap: "16px",
            borderColor: theme.palette.grey[300],
          },
          "&.MuiAlert-root": {
            backgroundColor: theme.palette.warning.light,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: "none",
          color: theme.palette.secondary.dark,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          // backgroundColor: "#fff",
          opacity: "0.95",
        },
      },
    },
    MuiStep: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root.MuiStepIcon-root.Mui-completed": {
            // Style for the completed step icon
            color: theme.palette.success[400],
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          "&.button--style": {
            "height": "35px",
            "padding": "5px",
            "&:hover": {
              fontWeight: 600,
              cursor: "pointer",
            },
          },
          "&.button--style--primary": {
            "cursor": "pointer",
            "transition": "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
            },
            "height": "35px",
            "padding": "5px",
            "borderRadius": "10px",
          },
        },
        sizeMedium: {
          fontWeight: 500,
          fontSize: "0.875rem",
          lineHeight: "1.75",
          letterSpacing: "0.02857em",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: "8px",
          lineHeight: "1",
          "&.dark--button": {
            color: "#fff",
            backgroundColor: theme.palette.grey[900],
            /* "&:hover": {
              backgroundColor: theme.palette.grey[700],
              color: theme.palette.grey[200],
            }, */
          },
          // Rule for the disabled state
          "&.Mui-disabled": {
            color: theme.palette.grey[500], // Disabled text color
            backgroundColor: theme.palette.grey[300], // Disabled background color
            opacity: 0.5,
          },
          "&.yellow--button": {
            "&:hover": {
              backgroundColor: theme.palette.warning[400],
              color: theme.palette.warning.dark,
            },
          },
        },
        sizeMedium: {
          fontSize: "14px",
          padding: "11px 20px",
        },
        sizeSmall: {
          fontSize: "12px",
          padding: "10px 16px",
        },
        sizeLarge: {
          fontSize: "16px",
          padding: "12px 24px",
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          //const shadowColor =
          //  theme.palette[ownerState.color]?.main || theme.palette.primary.main;
          return {
            "color": "#FFF",
            "fontFamily": "inherit",
            "boxShadow": "none", // `${shadowColor}40 0px 8px 16px 0px`, // 40 = 25% opacity
            "borderRadius": "10px",
            "padding": "10px",
            "&:hover": {
            //  boxShadow: "none",
            },
          };
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&.dark--button": {
            color: "#fff",
            backgroundColor: theme.palette.grey[900],
            "&:hover": {
              backgroundColor: theme.palette.grey[700],
              color: theme.palette.grey[200],
            },
          },
          "&.green--button": {
            color: "#fff",
            backgroundColor: theme.palette.success[800],
            "&:hover": {
              backgroundColor: theme.palette.success[700],
              color: "#fff",
            },
          },
          "&.blue--button": {
            color: "#fff",
            backgroundColor: theme.palette.secondary.dark,
            "&:hover": {
              backgroundColor: theme.palette.success[700],
              color: "#fff",
            },
          },
          "&.blue--button--light": {
            color: "#fff",
            backgroundColor: theme.palette.secondary[600],
            "&:hover": {
              backgroundColor: theme.palette.success[700],
              color: "#fff",
            },
          },
          "&.red--button": {
            color: "#fff",
            backgroundColor: theme.palette.error.dark,
            "&:hover": {
              backgroundColor: theme.palette.error[700],
              color: "#fff",
            },
          },
          "&.yellow--button": {
            color: "#fff",
            backgroundColor: theme.palette.warning.darker,
            "&:hover": {
              backgroundColor: theme.palette.warning.light,
              color: "#fff",
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          padding: "12px",
          borderRadius: "16px",
          // "boxShadow": "rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px",
          "&.background--neutral": {
            backgroundColor: theme.palette.grey.neutral,
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          color: theme.palette.text.dark,
          padding: "24px",
        },
        title: {
          fontSize: "1.125rem",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "24px",
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: "16px",
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          paddingBottom: "15px",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: theme.palette.grey[500],
          paddingTop: "4px",
          paddingBottom: "4px",
          paddingLeft: "12px",
          paddingRight: "8px",
          minWidth: "64px",
          "&.Mui-selected": {
            color: theme.palette.grey[900],
            backgroundColor: theme.palette.grey[50],
            "&:hover": {
              backgroundColor: theme.palette.grey[50],
              borderRadius: theme.shape.borderRadius,
            },
            "& .MuiListItemIcon-root": {
              color: theme.palette.grey[900],
            },
          },
          "&:hover": {
            backgroundColor: theme.palette.grey[100],
            color: theme.palette.grey[900],
            "& .MuiListItemIcon-root": {
              color: theme.palette.grey[900],
            },
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontWeight: "600",
          fontSize: "0.875rem",
          lineHeight: 1.71429,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: theme.palette.grey[500],
          minWidth: "36px",
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          color: theme.palette.grey[500],
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          "& .MuiAccordionSummary-content > p": {
            fontWeight: "600",
            color: theme.palette.grey[700],
          },
          "& .MuiSvgIcon-root": {
            color: theme.palette.grey[700],
          },
          "& .MuiAccordionSummary-expandIconWrapper": {
            padding: "1em",
          },
          "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
            transform: "rotate(45deg)",
          },
          "& .MuiButtonBase-root": {
            backgroundColor: theme.palette.grey[50],
          },
          "& .MuiAccordionDetails-root": {
            padding: "8px 38px 16px",
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          overflow: "hidden",
          "& .MuiPaper-root ": {
            boxShadow: "none",
          },
          "& .MuiTableHead-root": {
            backgroundColor: theme.palette.grey[50],
            borderColor: theme.palette.grey[100],
            // '& .MuiTableRow-root & .MuiTableRow-head': {
            // backgroundColor: theme.colors?.grey800,
            // },
          },
          "& .MuiTableRow-root": {
            "&:nth-of-type(even)": {
              backgroundColor: theme.palette.grey[50],
            },
          },
          "& .MuiTableCell-head": {
            fontWeight: "600",
          },
          "& .MuiTableCell-root": {
            // borderColor:theme.colors?.grey300,
            borderBottom: "none",
          },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        size: "small",
      },
      components: {
        icon: CustomIcon,
        checkedIcon: CheckedIcon,
        indeterminateIcon: IndeterminateIcon,
      },
    },
    // add here
  },
});

export default theme;
